/**
 * The scratchpad's shared vocabulary — everything the API and the browser must
 * agree on.
 *
 * This module exists to hold that boundary, and it may not import anything.
 * The modules doing the real work are server-only by construction: source.ts
 * reaches for `node:fs`, transform.ts loads Babel. The web app needs the
 * registry keys and the failure shape, and a client bundle that imported either
 * of those files to get a constant would drag the database driver and a
 * compiler into the browser.
 *
 * Reached from the web app as `@starter/api/scratchpad`.
 */

/** Where mockups live, relative to the repository root. */
export const MOCKUP_DIR = 'scratchpad';

/** Global the host page publishes the module registry on. */
export const REGISTRY_GLOBAL = '__STARTER_SCRATCHPAD__';

/**
 * Everything a mockup may import. Anything else fails to compile, with a line
 * number, on the page.
 *
 * This list is the sandbox: a specifier absent from it does not resolve, so a
 * mockup structurally cannot reach the API client, the router or the query
 * layer. Widening it is a deliberate edit here *and* in the web app's registry
 * (apps/web/src/scratchpad/registry.ts), which is where these keys are bound to
 * real modules — the test in that file's package fails if the two disagree.
 */
export const SCRATCHPAD_REGISTRY_KEYS = [
  '@starter/ui',
  'react',
  // Injected by Babel's automatic JSX runtime into its own output, not written
  // by the author. Without it every mockup fails on its first element.
  'react/jsx-runtime',
] as const;

/**
 * Feature names address a file path, so they are restricted to characters that
 * cannot climb out of the mockup directory or smuggle a query string into the
 * upstream URL.
 */
export function isValidFeatureName(feature: string): boolean {
  return /^[a-z0-9][a-z0-9-]{0,63}$/.test(feature);
}

/** Where a mockup lives, as a URL. One implementation, used by both apps. */
export function scratchpadPath(feature: string): string {
  return `/scratchpad/${feature}`;
}

/** One mockup as the page needs to list it. */
export interface MockupListEntry {
  feature: string;
  description?: string;
}

/**
 * Why a mockup is not on screen. Compiled into the served module rather than
 * thrown, so the page can render the reason with a line number instead of
 * blank-screening. See module.ts.
 */
export interface MockupFailure {
  kind: 'compile' | 'missing' | 'unconfigured' | 'unknown';
  message: string;
  line?: number;
  detail?: string;
}
